import { useState } from "react";
import http from "../../../api/apiClient";
import { API_PATH } from "../constants";
import type { OfficialSchoolHoliday } from "../types";
import { buildCalendarDateTime, normalizeModalError, normalizeOfficialHolidays } from "../utils";

interface UseOfficialHolidaysArgs {
  t: any;
  canAdd: boolean;
  selectedSchoolYearUuid: string;
  selectedSchoolYearApiLabel: string;
  loadEvents: () => Promise<void>;
  showToast: (message: string, type?: "success" | "error" | "info") => void;
  openSchoolYearWarning: () => void;
}

export const useOfficialHolidays = ({
  t,
  canAdd,
  selectedSchoolYearUuid,
  selectedSchoolYearApiLabel,
  loadEvents,
  showToast,
  openSchoolYearWarning,
}: UseOfficialHolidaysArgs) => {
  const [officialHolidayModalOpen, setOfficialHolidayModalOpen] = useState(false);
  const [officialHolidays, setOfficialHolidays] = useState<OfficialSchoolHoliday[]>([]);
  const [selectedOfficialHolidayIds, setSelectedOfficialHolidayIds] = useState<string[]>([]);
  const [officialHolidaysLoading, setOfficialHolidaysLoading] = useState(false);
  const [officialHolidaysSaving, setOfficialHolidaysSaving] = useState(false);
  const [officialHolidaysError, setOfficialHolidaysError] = useState("");

  const openOfficialHolidayModal = async () => {
    if (!selectedSchoolYearUuid || !selectedSchoolYearApiLabel) {
      openSchoolYearWarning();
      return;
    }

    setOfficialHolidayModalOpen(true);
    setOfficialHolidays([]);
    setSelectedOfficialHolidayIds([]);
    setOfficialHolidaysError("");
    setOfficialHolidaysLoading(true);

    try {
      const { data: payload } = await http.get(
        `${API_PATH}/official-public-holidays/schoolyear/${encodeURIComponent(selectedSchoolYearApiLabel)}`,
      );

      const holidays = normalizeOfficialHolidays(payload);
      setOfficialHolidays(holidays);
      setSelectedOfficialHolidayIds(holidays.map((holiday) => holiday.id));

      if (holidays.length === 0) {
        setOfficialHolidaysError(
          t("schoolCalendar.officialHolidays.empty", {
            defaultValue: "Er zijn geen feestdagen gevonden voor dit schooljaar.",
          }),
        );
      }
    } catch {
      setOfficialHolidaysError(
        t("schoolCalendar.officialHolidays.loadFailed", {
          defaultValue: "Feestdagen ophalen is mislukt. Controleer of de backend bereikbaar is.",
        }),
      );
    } finally {
      setOfficialHolidaysLoading(false);
    }
  };

  const toggleOfficialHolidaySelection = (holidayId: string) => {
    setSelectedOfficialHolidayIds((current) =>
      current.includes(holidayId)
        ? current.filter((id) => id !== holidayId)
        : [...current, holidayId],
    );
  };

  const saveOfficialHolidays = async () => {
    if (!canAdd || !selectedSchoolYearUuid) return;

    const holidaysToCreate = officialHolidays.filter((holiday) =>
      selectedOfficialHolidayIds.includes(holiday.id),
    );

    if (holidaysToCreate.length === 0) {
      setOfficialHolidaysError(
        t("schoolCalendar.officialHolidays.selectionRequired", {
          defaultValue: "Selecteer minimaal één feestdag om toe te voegen.",
        }),
      );
      return;
    }

    setOfficialHolidaysSaving(true);
    setOfficialHolidaysError("");

    try {
      let created = 0;
      let skipped = 0;

      for (const holiday of holidaysToCreate) {
        try {
          await http.post(API_PATH, {
            scope: "SCHOOL",
            classroomUuids: [],
            schoolYearUuid: selectedSchoolYearUuid,
            title:
              holiday.region && holiday.region !== "Nederland"
                ? `${holiday.title} (${holiday.region})`
                : holiday.title,
            description: [
              t("schoolCalendar.officialHolidays.source", {
                defaultValue: "Bron: Lumen berekende Nederlandse feestdagen",
              }),
              holiday.notice,
            ]
              .filter(Boolean)
              .join("\n"),
            eventType: "HOLIDAY",
            startDate: buildCalendarDateTime(holiday.startDate, "", true, false),
            endDate: buildCalendarDateTime(holiday.endDate, "", true, true),
            allDay: true,
            location: holiday.region,
            published: false,
          });

          created += 1;
        } catch (err: any) {
          if (err?.response?.status === 409) {
            skipped += 1;
            continue;
          }

          throw err;
        }
      }

      setOfficialHolidayModalOpen(false);
      await loadEvents();
      showToast(
        skipped > 0
          ? `${created} feestdagen toegevoegd, ${skipped} overgeslagen omdat ze al bestaan.`
          : t("schoolCalendar.officialHolidays.saved", {
              count: created,
              defaultValue: "Geselecteerde feestdagen zijn toegevoegd.",
            }),
        "success",
      );
    } catch (err) {
      const normalized = normalizeModalError(
        err,
        t,
        t("schoolCalendar.officialHolidays.saveFailed", {
          defaultValue: "Feestdagen toevoegen is mislukt.",
        }),
      );
      setOfficialHolidaysError(normalized.message);
    } finally {
      setOfficialHolidaysSaving(false);
    }
  };

  return {
    officialHolidayModalOpen,
    setOfficialHolidayModalOpen,
    officialHolidays,
    selectedOfficialHolidayIds,
    officialHolidaysLoading,
    officialHolidaysSaving,
    officialHolidaysError,
    openOfficialHolidayModal,
    toggleOfficialHolidaySelection,
    saveOfficialHolidays,
  };
};
